import { EmptyState } from '@/components/common/empty-state'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SkeletonRow } from '@/components/common/skeleton-row'
import { CreateInvitationButton } from '@/features/members/components/CreateInvitationButton'
import { RevokeInvitationDialog } from '@/features/members/components/DeleteInvitationDialog'
import { useInvitations } from '@/features/members/hooks/useInvitations'
import { useMembers } from '@/features/members/hooks/useMembers'
import { Mail, Users, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'

export const MemberList = () => {
  const {
    data: members,
    isLoading: isLoadingMembers,
    error: membersError,
    isError: isMembersError,
  } = useMembers()
  const {
    data: invitations,
    isLoading: isLoadingInvitations,
    error: invitationsError,
    isError: isInvitationsError,
  } = useInvitations()

  const [revokeInvitationId, setRevokeInvitationId] = useState<string | null>(
    null,
  )

  useEffect(() => {
    if (!isLoadingMembers && isMembersError) {
      toast.error('Failed to load members', {
        description:
          membersError instanceof Error
            ? membersError.message
            : 'Please try again or contact support',
      })
    }
  }, [isLoadingMembers, isMembersError, membersError])

  useEffect(() => {
    if (!isLoadingInvitations && isInvitationsError) {
      toast.error('Failed to load invitations', {
        description:
          invitationsError instanceof Error
            ? invitationsError.message
            : 'Please try again or contact support',
      })
    }
  }, [isLoadingInvitations, isInvitationsError, invitationsError])

  return (
    <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="mb-8 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Members</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Invite and manage your organization members
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <CreateInvitationButton />
        </div>
      </div>

      <Card>
        <CardHeader className="border-b">
          <CardTitle>Active Members</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {isLoadingMembers ? (
            <SkeletonRow count={2} />
          ) : members?.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No active members"
              description="Invite team members to collaborate with you."
            />
          ) : (
            <div className="divide-y divide-border">
              {members?.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center py-3 first:pt-0 last:pb-0"
                >
                  <span className="text-sm text-foreground w-[200px] pr-4">
                    <span className="block truncate">{member.email}</span>
                  </span>
                  <Badge variant="secondary" className="capitalize shrink-0">
                    {member.role}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader className="border-b">
          <CardTitle>Pending Invitations</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {isLoadingInvitations ? (
            <SkeletonRow count={2} />
          ) : invitations?.length === 0 ? (
            <EmptyState
              icon={Mail}
              title="No pending invitations"
              description="Invite team members to collaborate with you."
            />
          ) : (
            <div className="divide-y divide-border">
              {invitations?.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center py-3 first:pt-0 last:pb-0"
                >
                  <span className="text-sm text-foreground w-[200px] pr-4">
                    <span className="block truncate">{invitation.email}</span>
                  </span>
                  <Badge variant="secondary" className="capitalize shrink-0">
                    {invitation.state}
                  </Badge>
                  <div className="flex-1" />
                  <Button
                    variant="destructive"
                    size="sm"
                    aria-label="Revoke invitation"
                    className="inline-flex items-center px-3 py-1 text-sm font-medium leading-4 rounded-md border border-transparent"
                    onClick={() => setRevokeInvitationId(invitation.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <RevokeInvitationDialog
        invitationId={revokeInvitationId}
        onClose={() => setRevokeInvitationId(null)}
      />
    </div>
  )
}
